/** Routines — server-synced (all devices see the same list). Falls back to
 *  localStorage in demo mode; one-time migration of old local routines. */
import { api, isDemoMode } from "../api/client";

export interface Routine {
  id: string;
  title: string;
  kind: string;
  /** 0=Sunday … 6=Saturday, matching Date.getDay(). */
  days: number[];
  time: string | null;
}

const LEGACY_KEY = "nelurio.routines.v1";
const LEGACY_DONE_KEY = "nelurio.routines.done.v1";
const MIGRATED_KEY = "nelurio.routines.migrated.v1";

function localDateKey(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function readLegacy(): Routine[] {
  try {
    const value = JSON.parse(window.localStorage.getItem(LEGACY_KEY) ?? "[]");
    return Array.isArray(value) ? value.filter((item) => item && item.id && item.title) : [];
  } catch {
    return [];
  }
}

/** Migrate localStorage routines + completions to the server exactly once. */
async function migrateLegacy() {
  if (isDemoMode()) return;
  if (window.localStorage.getItem(MIGRATED_KEY)) return;
  const legacy = readLegacy();
  try {
    for (const routine of legacy) {
      await api("/routines", { method: "POST", body: JSON.stringify({ title: routine.title, kind: routine.kind ?? "study", days: routine.days ?? [], time: routine.time ?? "" }) });
    }
    // carry over recent completions (last 7 days) so journals stay intact
    try {
      const doneMap: Record<string, string[]> = JSON.parse(window.localStorage.getItem(LEGACY_DONE_KEY) ?? "{}");
      const server = await api<{ routines: Array<{ id: string; title: string }> }>("/routines");
      const byTitle = new Map(server.routines.map((item) => [item.title, item.id]));
      for (let offset = 0; offset < 7; offset += 1) {
        const day = localDateKey(new Date(Date.now() - offset * 86400000));
        for (const routineId of doneMap[day] ?? []) {
          const legacyRoutine = legacy.find((item) => item.id === routineId);
          const serverId = legacyRoutine ? byTitle.get(legacyRoutine.title) : undefined;
          if (serverId) await api(`/routines/${serverId}/toggle`, { method: "POST", body: JSON.stringify({ day, done: true }) }).catch(() => undefined);
        }
      }
    } catch { /* completions are best-effort */ }
    window.localStorage.setItem(MIGRATED_KEY, new Date().toISOString());
  } catch { /* retry next launch */ }
}

export async function fetchRoutines(dayKey?: string): Promise<{ routines: Routine[]; done: string[] }> {
  if (isDemoMode()) {
    return { routines: readLegacy(), done: [] };
  }
  await migrateLegacy();
  const data = await api<{ routines: Routine[]; done: string[] }>(`/routines${dayKey ? `?date=${dayKey}` : ""}`);
  return { routines: Array.isArray(data.routines) ? data.routines : [], done: Array.isArray(data.done) ? data.done : [] };
}

export async function saveRoutine(routine: Routine, isNew: boolean): Promise<void> {
  if (isDemoMode()) {
    const list = readLegacy().filter((item) => item.id !== routine.id);
    writeLegacy([...list, routine]);
    return;
  }
  const payload = JSON.stringify({ title: routine.title, kind: routine.kind, days: routine.days, time: routine.time ?? "" });
  if (isNew) await api("/routines", { method: "POST", body: payload });
  else await api(`/routines/${routine.id}`, { method: "PATCH", body: payload });
}

export async function removeRoutine(routineId: string): Promise<void> {
  if (isDemoMode()) {
    writeLegacy(readLegacy().filter((item) => item.id !== routineId));
    return;
  }
  await api(`/routines/${routineId}`, { method: "DELETE" });
}

export async function toggleRoutineDoneApi(routineId: string, dayKey: string, done: boolean): Promise<void> {
  if (isDemoMode()) return;
  await api(`/routines/${routineId}/toggle`, { method: "POST", body: JSON.stringify({ day: dayKey, done }) });
}

function writeLegacy(list: Routine[]) {
  try { window.localStorage.setItem(LEGACY_KEY, JSON.stringify(list)); } catch { /* ignore */ }
}

// Legacy sync helpers still used by demo mode + calendar dots:
export function readRoutines(): Routine[] {
  return readLegacy();
}

export function writeRoutines(list: Routine[]) {
  writeLegacy(list);
}

export { routinesForDate, WEEKDAY_COUNT } from "./routineSchedule";
